import { useEffect, useState, type CSSProperties } from "react";
import { Button, Dialog, Input, Select } from "../components";
import { Icon, useLucideIcons } from "../components/Icon";
import type { Student } from "../lib/types";

// ── Calendar helpers ────────────────────────────────────────────────────
const WEEK_LABELS = ["一", "二", "三", "四", "五", "六", "日"];
const MONTH_NAMES = ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"];
const DAY_LABELS = ["周一", "周二", "周三", "周四", "周五", "周六", "周日"];

function getDaysInMonth(y: number, m: number) { return new Date(y, m + 1, 0).getDate(); }
function getFirstDow(y: number, m: number) { return (new Date(y, m, 1).getDay() + 6) % 7; }
function toDateString(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ── Mini calendar picker ────────────────────────────────────────────────
function MiniCalendar({ value, onChange }: { value: Date; onChange: (d: Date) => void }) {
  const [viewYear, setViewYear] = useState(value.getFullYear());
  const [viewMonth, setViewMonth] = useState(value.getMonth());
  const today = new Date();

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDow = getFirstDow(viewYear, viewMonth);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <button type="button" onClick={prevMonth} style={navBtnStyle} aria-label="上一月">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text-strong)" }}>
          {viewYear}年{MONTH_NAMES[viewMonth]}
        </span>
        <button type="button" onClick={nextMonth} style={navBtnStyle} aria-label="下一月">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", textAlign: "center", marginBottom: 2 }}>
        {WEEK_LABELS.map((label, i) => (
          <div key={label} style={{ fontSize: 11, fontWeight: 700, color: i >= 5 ? "var(--accent)" : "var(--text-subtle)", padding: "3px 0" }}>
            {label}
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, textAlign: "center" }}>
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const isSel = day === value.getDate() && viewYear === value.getFullYear() && viewMonth === value.getMonth();
          const isToday = day === today.getDate() && viewYear === today.getFullYear() && viewMonth === today.getMonth();
          return (
            <button
              key={day}
              type="button"
              onClick={() => onChange(new Date(viewYear, viewMonth, day))}
              style={{
                border: "none",
                borderRadius: "50%",
                width: 30,
                height: 30,
                margin: "1px auto",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: isSel || isToday ? 700 : 400,
                background: isSel ? "var(--brand)" : "transparent",
                color: isSel ? "#fff" : isToday ? "var(--brand)" : "var(--text-body)",
                outline: isToday && !isSel ? "1.5px solid var(--brand)" : "none",
                outlineOffset: -1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const navBtnStyle: CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  padding: "4px 6px",
  borderRadius: "var(--radius-sm)",
  color: "var(--text-muted)",
  display: "flex",
  alignItems: "center",
};

// ── Attendance ─────────────────────────────────────────────────────────
type AttendStatus = "upcoming" | "taught" | "leave" | "absent";

const ATTEND_OPTIONS: Array<{
  value: AttendStatus;
  label: string;
  fg: string;
  bg: string;
  border: string;
}> = [
  { value: "upcoming", label: "待上课", fg: "var(--text-muted)",        bg: "var(--surface-sunken)",   border: "var(--border-default)" },
  { value: "taught",   label: "已上课", fg: "var(--status-taught-fg)",  bg: "var(--status-taught-bg)", border: "var(--status-taught-fg)" },
  { value: "leave",    label: "请假",   fg: "var(--status-leave-fg)",   bg: "var(--status-leave-bg)",  border: "var(--status-leave-fg)" },
  { value: "absent",   label: "缺勤",   fg: "var(--status-absent-fg)",  bg: "var(--status-absent-bg)", border: "var(--status-absent-fg)" },
];

// ── Public types ────────────────────────────────────────────────────────
export interface ScheduleFormData {
  studentIds: number[];
  subject: string;
  type: string;
  startDate: string;
  startTime: string;
  endTime: string;
  repeat: string;
  repeatDays: number[];
  endType: string;
  endDate: string;
  endCount: number;
  studentStatuses: Record<number, AttendStatus>;
  saveScope: "one" | "future";
}

export interface ScheduleDialogProps {
  open: boolean;
  students: Student[];
  editEvent?: import("../lib/types").ScheduleEvent;
  defaultStudentId?: number;
  defaultDate?: Date;
  defaultTime?: string;
  onClose: () => void;
  onSave: (data: ScheduleFormData) => void;
  onDelete?: (eventId: number) => void;
  onDeleteFuture?: (studentIds: number[], fromDate: string) => void;
}

// ── Dialog component ────────────────────────────────────────────────────
export function ScheduleDialog({
  open,
  students,
  editEvent,
  defaultStudentId,
  defaultDate,
  defaultTime,
  onClose,
  onSave,
  onDelete,
  onDeleteFuture,
}: ScheduleDialogProps) {
  const today = new Date();
  const isEditMode = !!editEvent;

  const [studentIds, setStudentIds] = useState<number[]>(
    defaultStudentId !== undefined ? [defaultStudentId] : students[0] ? [students[0].id] : []
  );
  const [subject, setSubject] = useState("数学");
  const [classType, setClassType] = useState("一对一");
  const [startDate, setStartDate] = useState(defaultDate ?? today);
  const [startTime, setStartTime] = useState(defaultTime ?? "16:00");
  const [endTime, setEndTime] = useState("17:30");
  const [repeat, setRepeat] = useState("weekly");
  const [repeatDays, setRepeatDays] = useState<number[]>([0, 2, 4]);
  const [endType, setEndType] = useState("count");
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 3);
    return toDateString(d);
  });
  const [endCountStr, setEndCountStr] = useState("20");
  const endCount = Math.max(1, parseInt(endCountStr, 10) || 1);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [studentStatuses, setStudentStatuses] = useState<Record<number, AttendStatus>>({});

  useLucideIcons([open, repeat, endType, isEditMode, classType]);

  // Sync props → state each time the dialog opens
  useEffect(() => {
    if (!open) return;
    setConfirmDelete(false);

    if (editEvent) {
      const ids = editEvent.studentIds?.length ? editEvent.studentIds : [editEvent.student_id];
      setStudentIds(ids);
      // Initialise per-student attendance from saved data or fall back to event status
      const initStatuses: Record<number, AttendStatus> = {};
      for (const id of ids) {
        initStatuses[id] = (editEvent.studentStatuses?.[id] as AttendStatus | undefined)
          ?? (editEvent.status as AttendStatus)
          ?? "upcoming";
      }
      setStudentStatuses(initStatuses);
      const parts = editEvent.subject.split(" · ");
      if (parts[0]) setSubject(parts[0]);
      const ct = parts[1] ?? "一对一";
      setClassType(ct);
      setStartTime(editEvent.slot);
      setEndTime(editEvent.end_time);
      if (editEvent.repeatConfig) {
        setRepeat(editEvent.repeatConfig.repeat);
        setRepeatDays(editEvent.repeatConfig.repeatDays);
        setEndType(editEvent.repeatConfig.endType);
        setEndDate(editEvent.repeatConfig.endDate);
        setEndCountStr(String(editEvent.repeatConfig.endCount));
      } else {
        setRepeat("none");
        setRepeatDays([editEvent.day]);
      }
      setStartDate(editEvent.date ? new Date(editEvent.date + "T00:00:00") : today);
      return;
    }

    // New mode — reset all fields to clean defaults
    setStudentStatuses({});
    if (defaultStudentId !== undefined) setStudentIds([defaultStudentId]);
    else if (students[0]) setStudentIds([students[0].id]);

    setStartDate(defaultDate ?? today);
    if (defaultDate) {
      const dow = (defaultDate.getDay() + 6) % 7;
      setRepeatDays([dow]);
    } else {
      setRepeatDays([0, 2, 4]);
    }

    setStartTime(defaultTime ?? "16:00");
    if (defaultTime) {
      const [h, m] = defaultTime.split(":").map(Number);
      const end = new Date(0, 0, 0, h + 1, m + 30);
      setEndTime(`${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`);
    } else {
      setEndTime("17:30");
    }

    setRepeat("weekly");
    setEndType("count");
    setEndCountStr("20");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editEvent, defaultStudentId, defaultDate, defaultTime]);

  const handleClassTypeChange = (type: string) => {
    setClassType(type);
    if (type === "一对一") {
      if (studentIds.length > 1) setStudentIds([studentIds[0]]);
    } else {
      setStudentIds([]);
    }
  };

  const toggleDay = (d: number) =>
    setRepeatDays(days => days.includes(d) ? days.filter(x => x !== d) : [...days, d].sort());

  const toggleStudent = (id: number) => {
    if (classType === "一对一") {
      setStudentIds([id]);
    } else {
      setStudentIds(prev =>
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
      );
    }
  };

  const handleSave = (scope: "one" | "future" = "one") => {
    onSave({
      studentIds,
      subject,
      type: classType,
      startDate: toDateString(startDate),
      startTime,
      endTime,
      repeat,
      repeatDays,
      endType,
      endDate,
      endCount,
      studentStatuses,
      saveScope: scope,
    });
  };

  if (!open) return null;

  const isGroupClass = classType === "班课" || classType === "小班";
  const selectedStudents = students.filter(s => studentIds.includes(s.id));
  const selectedGrade = isGroupClass && studentIds.length > 0
    ? students.find(s => studentIds.includes(s.id))?.grade
    : undefined;
  const groupStudentList = selectedGrade
    ? students.filter(s => s.grade === selectedGrade)
    : students;
  const todayDateStr = toDateString(new Date());
  const isPastEvent = isEditMode && !!editEvent?.date && editEvent.date < todayDateStr;
  const isSeries = isEditMode && (!!editEvent?.seriesId || !!editEvent?.repeatConfig);

  const studentLabel = selectedStudents.length === 1
    ? selectedStudents[0].name
    : `${selectedStudents.length}名学员`;

  const footer = isEditMode ? (
    <>
      {confirmDelete ? (
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 12, color: "var(--text-subtle)" }}>
            选择删除范围 · <span style={{ color: "var(--text-strong)", fontWeight: 600 }}>{editEvent?.date}</span>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <Button variant="secondary" size="sm" onClick={() => setConfirmDelete(false)}>取消</Button>
            <Button variant="danger" size="sm" onClick={() => onDelete?.(editEvent!.id)}>
              仅删除这节
            </Button>
            <Button variant="danger" size="sm" onClick={() => onDeleteFuture?.(studentIds, editEvent?.date ?? "")}>
              删除{studentLabel}此后所有课程
            </Button>
          </div>
        </div>
      ) : (
        <>
          <Button variant="ghost" onClick={() => setConfirmDelete(true)} style={{ color: "var(--danger-fg)" }}>
            删除课程
          </Button>
          <div style={{ flex: 1 }} />
          <Button variant="secondary" onClick={onClose}>取消</Button>
          {isSeries && !isPastEvent ? (
            <>
              <Button variant="secondary" onClick={() => handleSave("one")}>仅此次</Button>
              <Button variant="primary" onClick={() => handleSave("future")}>此后所有</Button>
            </>
          ) : (
            <Button variant="primary" onClick={() => handleSave("one")}>
              {isPastEvent ? "保存出勤" : "保存修改"}
            </Button>
          )}
        </>
      )}
    </>
  ) : (
    <>
      <Button variant="secondary" onClick={onClose}>取消</Button>
      <Button variant="primary" onClick={() => handleSave("one")}>保存排课</Button>
    </>
  );

  return (
    <Dialog
      open={open}
      icon={<Icon name={isEditMode ? "calendar-check" : "calendar-plus"} size={20} />}
      title={isEditMode ? "编辑排课" : "新建排课"}
      onClose={onClose}
      closeOnBackdrop={false}
      footer={footer}
    >
      <div style={{ overflowY: "auto", maxHeight: "calc(80vh - 180px)", paddingBottom: 4, marginTop: 8 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {(!isEditMode || !isPastEvent) && (<>
          {/* Subject & type — shown first so classType affects student picker below */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Select
              label="科目"
              value={subject}
              options={[
                { value: "数学", label: "数学" },
                { value: "语文", label: "语文" },
                { value: "英语", label: "英语" },
                { value: "物理", label: "物理" },
                { value: "化学", label: "化学" },
              ]}
              onChange={e => setSubject(e.target.value)}
            />
            <Select
              label="课型"
              value={classType}
              options={[
                { value: "一对一", label: "一对一" },
                { value: "班课", label: "班课" },
                { value: "小班", label: "小班" },
              ]}
              onChange={e => handleClassTypeChange(e.target.value)}
            />
          </div>

          {/* Student selector — single for 一对一, multi-checkbox for group */}
          {!isGroupClass ? (
            <Select
              label="学员"
              value={String(studentIds[0] ?? 0)}
              options={students.map(s => ({ value: String(s.id), label: `${s.name} · ${s.grade}` }))}
              onChange={e => setStudentIds([Number(e.target.value)])}
            />
          ) : (
            <div>
              <div style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--text-subtle)",
                marginBottom: 8,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}>
                学员（多选）
                {studentIds.length > 0 && (
                  <span style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--brand)",
                    background: "var(--brand-subtle)",
                    padding: "1px 7px",
                    borderRadius: 99,
                  }}>
                    已选 {studentIds.length} 人
                  </span>
                )}
                {selectedGrade && (
                  <span style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-muted)",
                    marginLeft: "auto",
                  }}>
                    仅显示 {selectedGrade}
                  </span>
                )}
              </div>
              <div style={{
                maxHeight: 176,
                overflowY: "auto",
                border: "1.5px solid var(--border-default)",
                borderRadius: "var(--radius-md)",
                background: "var(--surface-card)",
              }}>
                {groupStudentList.map((s, idx) => {
                  const checked = studentIds.includes(s.id);
                  return (
                    <div
                      key={s.id}
                      role="checkbox"
                      aria-checked={checked}
                      tabIndex={0}
                      onClick={() => toggleStudent(s.id)}
                      onKeyDown={(e) => e.key === " " && toggleStudent(s.id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 12px",
                        cursor: "pointer",
                        borderBottom: idx < groupStudentList.length - 1 ? "1px solid var(--divider)" : "none",
                        background: checked ? "var(--brand-subtle)" : "transparent",
                        transition: "background 0.1s",
                        userSelect: "none",
                      }}
                    >
                      {/* Visual checkbox */}
                      <div style={{
                        width: 16,
                        height: 16,
                        borderRadius: 4,
                        border: `2px solid ${checked ? "var(--brand)" : "var(--border-default)"}`,
                        background: checked ? "var(--brand)" : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 0.12s",
                      }}>
                        {checked && (
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{
                          fontSize: 13,
                          fontWeight: checked ? 700 : 400,
                          color: checked ? "var(--brand)" : "var(--text-strong)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                          {s.name}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-subtle)" }}>
                          {s.grade} · {s.subject}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          </>)}

          {/* Past event: read-only info */}
          {isEditMode && isPastEvent && (
            <div style={{
              background: "var(--surface-sunken)",
              border: "1.5px solid var(--border-subtle)",
              borderRadius: "var(--radius-md)",
              padding: "12px 14px",
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-subtle)", marginBottom: 6 }}>
                历史课程 · 仅可调整出勤情况
              </div>
              <div style={{ fontSize: 13, color: "var(--text-strong)", fontWeight: 600 }}>
                {subject} · {classType}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                {selectedStudents.map(s => s.name).join("、")} · {editEvent?.date} {editEvent?.slot}–{editEvent?.end_time}
              </div>
            </div>
          )}

          {/* ── Attendance section — edit mode only ── */}
          {isEditMode && selectedStudents.length > 0 && (
            <div style={{
              background: "var(--surface-sunken)",
              border: "1.5px solid var(--border-subtle)",
              borderRadius: "var(--radius-md)",
              padding: "12px 14px",
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-subtle)", marginBottom: 10, display: "flex", alignItems: "center", gap: 5 }}>
                <Icon name="user-check" size={13} />
                出勤情况 · {editEvent?.date ?? ""} {editEvent?.slot}–{editEvent?.end_time}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {selectedStudents.map((s) => {
                  const cur = studentStatuses[s.id] ?? "upcoming";
                  return (
                    <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--text-strong)",
                        minWidth: 52,
                        flexShrink: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>
                        {s.name}
                      </span>
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                        {ATTEND_OPTIONS.map((opt) => {
                          const active = cur === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setStudentStatuses((prev) => ({ ...prev, [s.id]: opt.value }))}
                              style={{
                                padding: "3px 10px",
                                border: `1.5px solid ${active ? opt.border : "var(--border-default)"}`,
                                borderRadius: 99,
                                background: active ? opt.bg : "transparent",
                                color: active ? opt.fg : "var(--text-muted)",
                                fontSize: 11,
                                fontWeight: active ? 700 : 400,
                                cursor: "pointer",
                                transition: "all 0.12s",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(!isEditMode || !isPastEvent) && (<>
          {/* Calendar date picker */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-subtle)", marginBottom: 8 }}>开始日期</div>
            <div style={{
              background: "var(--surface-sunken)",
              borderRadius: "var(--radius-md)",
              border: "1.5px solid var(--border-subtle)",
              padding: "12px 14px",
            }}>
              <MiniCalendar value={startDate} onChange={setStartDate} />
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--divider)", fontSize: 13, color: "var(--text-muted)", textAlign: "center" }}>
                已选：<span style={{ fontWeight: 700, color: "var(--text-strong)" }}>{toDateString(startDate)}</span>
              </div>
            </div>
          </div>

          {/* Time range */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input
              label="开始时间"
              type="time"
              value={startTime}
              onChange={e => setStartTime(e.target.value)}
            />
            <Input
              label="结束时间"
              type="time"
              value={endTime}
              onChange={e => setEndTime(e.target.value)}
            />
          </div>

          {/* Repeat settings */}
          <div style={{ borderTop: "1.5px solid var(--divider)", paddingTop: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-strong)", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <Icon name="repeat" size={15} />
              重复设置
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Select
                label="重复方式"
                value={repeat}
                options={[
                  { value: "none", label: "不重复" },
                  { value: "weekly", label: "每周" },
                  { value: "biweekly", label: "每两周" },
                  { value: "monthly", label: "每月同一天" },
                ]}
                onChange={e => setRepeat(e.target.value)}
              />

              {(repeat === "weekly" || repeat === "biweekly") && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-subtle)", marginBottom: 8 }}>
                    每周上课日
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {DAY_LABELS.map((label, i) => {
                      const on = repeatDays.includes(i);
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => toggleDay(i)}
                          style={{
                            flex: 1,
                            height: 36,
                            border: "1.5px solid",
                            borderColor: on ? "var(--brand)" : "var(--border-default)",
                            borderRadius: "var(--radius-md)",
                            background: on ? "var(--brand-subtle)" : "transparent",
                            color: on ? "var(--brand)" : "var(--text-muted)",
                            fontWeight: on ? 700 : 400,
                            fontSize: 12,
                            cursor: "pointer",
                            transition: "all 0.15s",
                            padding: 0,
                          }}
                        >
                          {label.replace("周", "")}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {repeat !== "none" && (
                <>
                  <Select
                    label="结束方式"
                    value={endType}
                    options={[
                      { value: "never", label: "永不" },
                      { value: "date", label: "指定截止日期" },
                      { value: "count", label: "限定课时总数" },
                    ]}
                    onChange={e => setEndType(e.target.value)}
                  />
                  {endType === "date" && (
                    <Input
                      label="截止日期"
                      type="date"
                      value={endDate}
                      min={toDateString(startDate)}
                      onChange={e => setEndDate(e.target.value)}
                    />
                  )}
                  {endType === "count" && (
                    <Input
                      label="课时总数上限"
                      type="number"
                      value={endCountStr}
                      min="1"
                      hint={`每次 ${startTime}–${endTime}，共 ${endCount} 课时后停止`}
                      onChange={e => setEndCountStr(e.target.value)}
                      onBlur={() => setEndCountStr(String(Math.max(1, parseInt(endCountStr, 10) || 1)))}
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {/* Summary preview */}
          {selectedStudents.length > 0 && (
            <div style={{
              background: "var(--brand-subtle)",
              border: "1.5px solid var(--border-brand, var(--brand))",
              borderRadius: "var(--radius-md)",
              padding: "12px 14px",
              fontSize: 13,
              color: "var(--text-body)",
              lineHeight: 1.6,
            }}>
              <div style={{ fontWeight: 700, color: "var(--text-strong)", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
                <Icon name="info" size={13} />
                排课预览
              </div>
              <div>
                学员：{selectedStudents.map(s => s.name).join("、")}
                {selectedStudents.length > 1 && (
                  <span style={{ color: "var(--text-subtle)", fontSize: 12 }}> （{selectedStudents.length}人）</span>
                )}
              </div>
              <div>科目：{subject} · {classType}</div>
              <div>首次上课：{toDateString(startDate)} {startTime}–{endTime}</div>
              {repeat !== "none" && (
                <div>
                  重复：
                  {repeat === "weekly" ? "每周" : repeat === "biweekly" ? "每两周" : "每月"}
                  {repeatDays.length > 0 && (repeat === "weekly" || repeat === "biweekly")
                    ? "（" + repeatDays.map(d => DAY_LABELS[d]).join("、") + "）"
                    : ""}
                  {endType === "date" ? ` · 截止 ${endDate}` : endType === "count" ? ` · 共 ${endCount} 课时` : ""}
                </div>
              )}
            </div>
          )}
          </>)}
        </div>
      </div>
    </Dialog>
  );
}
